import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { ChakraProvider } from '@chakra-ui/react';
import { chakraExtendedTheme } from '@/styles/theme';
import { useSeoFetch } from '@/hooks/useSeoFetch';
import useSeoProject from '@/hooks/useSeoProject';
import { FetchApiIds } from '@/types';
import seoKeywordsService from '@/services/seoKeywords';
import seoPagesService from '@/services/seoPages';
import searchConsoleService, {
  GoogleConnectionStatus,
  GSCKeywordRow,
} from '@/services/searchConsole';
import seoImpactService from '@/services/seoImpact';
import seoCopilotService from '@/services/seoCopilot';
import seoAiService from '@/services/seoAi';
import seoAiCacheService from '@/services/seoAiCache';
import {
  SeoTrackedKeyword,
  SeoKeywordSnapshot,
  SeoPage,
  WpAppliedChange,
  SeoCopilotSession,
  SeoCopilotCycle,
} from '@/types/api/seo';
import ActivateCopilotModal from '@/components/ActivateCopilotModal';
import { useLivePosition } from '@/hooks/seo/useLivePosition';
import { useKeywordCache } from '@/hooks/seo/useKeywordCache';
import { useKeywordTasks } from '@/hooks/seo/useKeywordTasks';
import { usePageSync } from '@/hooks/seo/usePageSync';
import { useTargetPage } from '@/hooks/seo/useTargetPage';
import { useBacklinkProfile } from '@/hooks/seo/useBacklinkProfile';
import { useKeywordDifficulty } from '@/hooks/seo/useKeywordIntelligence';
import SeoKeywordDetailComponent from './SeoKeywordDetail.component';

type GSCKeywordData = {
  dailyTrend: Array<{
    date: string;
    clicks: number;
    impressions: number;
    ctr: number;
    position: number;
  }>;
  rankingPages: Array<{
    page: string;
    clicks: number;
    impressions: number;
    ctr: number;
    position: number;
  }>;
  totals: { clicks: number; impressions: number; avgCtr: number; avgPosition: number };
};

const SeoKeywordDetailPage = function (): JSX.Element {
  const { keywordId, projectId = '' } = useParams();
  const { project, setBreadcrumbDetail } = useSeoProject();
  const siteUrl = project?.siteUrl ?? null;
  const [days, setDays] = useState(90);

  // Core data fetching
  const {
    data: keyword,
    isLoading: isLoadingKeyword,
    isError: keywordError,
    refetch: refetchKeyword,
  } = useSeoFetch<SeoTrackedKeyword>([FetchApiIds.SEO_KEYWORD_BY_ID, keywordId], () =>
    seoKeywordsService.getById(keywordId as string)
  );
  const { data: snapshots } = useSeoFetch<SeoKeywordSnapshot[]>(
    [FetchApiIds.SEO_KEYWORD_SNAPSHOTS, keywordId, days],
    () => seoKeywordsService.getSnapshots({ keywordId: keywordId as string, days })
  );
  const { data: connectionStatus } = useSeoFetch<GoogleConnectionStatus>(
    [FetchApiIds.SEO_GOOGLE_CONNECTION],
    () => searchConsoleService.getConnectionStatus(),
    { retry: false }
  );
  const isConnected = !!connectionStatus?.isConnected;
  const { data: gscData, isLoading: isLoadingGSC } = useSeoFetch<GSCKeywordData>(
    ['seo-keyword-gsc-detail', keywordId, keyword?.keyword, days],
    () => searchConsoleService.getKeywordGSCData(siteUrl!, keyword!.keyword, days),
    { enabled: isConnected && !!siteUrl && !!keyword?.keyword, retry: false }
  );
  const { data: sitePages = [] } = useSeoFetch<GSCKeywordRow[]>(
    ['seo-site-pages', siteUrl],
    () => searchConsoleService.getKeywordsWithPages(siteUrl!, 28, 200),
    { enabled: isConnected && !!siteUrl, retry: false }
  );
  const { data: projectPages = [] } = useSeoFetch<SeoPage[]>(
    ['seo-project-pages', projectId],
    () => seoPagesService.getByProject(projectId),
    { enabled: !!projectId }
  );
  const { data: appliedChanges = [] } = useSeoFetch<WpAppliedChange[]>(
    ['seo-applied-changes', keywordId],
    () => seoImpactService.getByKeywordId(keywordId as string),
    { enabled: !!keywordId }
  );

  // Breadcrumb
  useEffect(() => {
    if (keyword?.keyword) setBreadcrumbDetail(keyword.keyword);
    return () => setBreadcrumbDetail(null);
  }, [keyword?.keyword, setBreadcrumbDetail]);

  // Extracted hooks
  const { livePosition, isLoading: livePositionLoading } = useLivePosition(
    keyword?.keyword,
    siteUrl
  );
  const {
    serpAnalysis,
    serpSummary,
    contentOptimization,
    internalLinks,
    handleSerpAnalysisComplete,
  } = useKeywordCache(keywordId);
  const { targetPageUrl, handleTargetPageChange } = useTargetPage(
    keyword,
    projectPages,
    gscData ?? null,
    keywordId,
    refetchKeyword
  );
  const {
    tasks: keywordTasks,
    isLoading: isLoadingTasks,
    refetch: refetchTasks,
    handleStatusChange: handleTaskStatusChange,
    handleApplyToWp: handleTaskApplyToWp,
  } = useKeywordTasks(keywordId, project, targetPageUrl, keyword?.latestSnapshot?.url ?? undefined);
  usePageSync(projectId, gscData ?? null, sitePages, keywordId, livePosition);

  // Ahrefs data
  const {
    latestSnapshot: ahrefsDomainSnapshot,
    newBacklinks,
    lostBacklinks,
  } = useBacklinkProfile(projectId || undefined);
  const kwArray = keyword?.keyword ? [keyword.keyword] : [];
  const { difficultyMap } = useKeywordDifficulty(kwArray);
  const ahrefsKd = keyword?.keyword ? difficultyMap.get(keyword.keyword.toLowerCase()) : undefined;

  const wpConnected = !!(project?.wpConnectedAt && project?.wpSiteUrl);
  const competitorUrls = (gscData?.rankingPages ?? []).map((p) => p.page);

  // Auto-trigger SERP competitor analysis when none is cached
  const serpAutoTriggered = useRef(false);
  useEffect(() => {
    if (serpAutoTriggered.current || serpAnalysis || !keyword?.keyword || !projectId || !keywordId)
      return;
    serpAutoTriggered.current = true;

    seoAiService
      .analyzeSerpCompetitors({
        keyword: keyword.keyword,
        competitorUrls,
        userSiteUrl: siteUrl ?? undefined,
      })
      .then((result) => {
        handleSerpAnalysisComplete(result);
        seoAiCacheService
          .saveSerpAnalysis({
            projectId,
            keywordId,
            keyword: keyword.keyword,
            serpResults: result.competitors,
            summary: result.summary,
            status: 'complete',
          })
          .catch(() => {});
      })
      .catch(() => {});
  }, [keyword?.keyword, projectId, keywordId, serpAnalysis]);

  // Copilot
  const [isCopilotModalOpen, setIsCopilotModalOpen] = useState(false);

  const { data: copilotSession, refetch: refetchCopilot } = useSeoFetch<SeoCopilotSession | null>(
    ['seo-copilot-session', keywordId],
    () => seoCopilotService.getSessionForKeyword(keywordId as string),
    { enabled: !!keywordId }
  );

  const { data: copilotCycles = [], refetch: refetchCycles } = useSeoFetch<SeoCopilotCycle[]>(
    ['seo-copilot-cycles', copilotSession?.id],
    () => seoCopilotService.getCycles(copilotSession!.id),
    { enabled: !!copilotSession?.id }
  );

  const handleActivateCopilot = useCallback(
    async ({ targetPosition, maxCycles }: { targetPosition: number; maxCycles: number }) => {
      if (!projectId || !keywordId) throw new Error('Missing project or keyword');
      const result = await seoCopilotService.startSession(
        projectId,
        keywordId,
        targetPosition,
        maxCycles,
        targetPageUrl
      );
      refetchCopilot();
      refetchCycles?.();
      refetchTasks?.();
      return result;
    },
    [projectId, keywordId, targetPageUrl, refetchCopilot, refetchCycles, refetchTasks]
  );

  const handlePauseCopilot = useCallback(async () => {
    if (!copilotSession) return;
    await seoCopilotService.pauseSession(copilotSession.id);
    refetchCopilot();
  }, [copilotSession, refetchCopilot]);

  const handleResumeCopilot = useCallback(async () => {
    if (!copilotSession) return;
    await seoCopilotService.resumeSession(copilotSession.id);
    refetchCopilot();
  }, [copilotSession, refetchCopilot]);

  const [isRerunning, setIsRerunning] = useState(false);
  const handleRerunAnalysis = useCallback(async () => {
    if (!copilotSession) return;
    setIsRerunning(true);
    try {
      await seoCopilotService.triggerCycle(copilotSession.id);
      refetchCopilot();
      refetchCycles?.();
      refetchTasks?.();
    } catch (err) {
      console.error('[SEOx] Rerun analysis failed:', err);
    } finally {
      setIsRerunning(false);
    }
  }, [copilotSession, refetchCopilot, refetchTasks]);

  return (
    <>
      <SeoKeywordDetailComponent
        keyword={keyword ?? null}
        snapshots={snapshots ?? []}
        gscData={gscData ?? null}
        isLoading={isLoadingKeyword}
        isLoadingGSC={isLoadingGSC && isConnected && !!siteUrl}
        notFound={keywordError}
        days={days}
        onDaysChange={setDays}
        projectId={projectId}
        keywordId={keywordId ?? ''}
        competitorUrls={competitorUrls}
        userSiteUrl={siteUrl ?? undefined}
        serpAnalysis={serpAnalysis}
        serpSummary={serpSummary}
        contentOptimization={contentOptimization}
        internalLinks={internalLinks}
        onSerpAnalysisComplete={handleSerpAnalysisComplete}
        sitePages={sitePages.map((p) => ({ page: p.page, keyword: p.keyword }))}
        targetPageUrl={targetPageUrl}
        projectPages={projectPages}
        onTargetPageChange={handleTargetPageChange}
        appliedChanges={appliedChanges}
        livePosition={livePosition}
        livePositionLoading={livePositionLoading}
        tasks={keywordTasks}
        tasksLoading={isLoadingTasks && !!keywordId}
        onTaskStatusChange={handleTaskStatusChange}
        onTaskApplyToWp={handleTaskApplyToWp}
        onTaskCreated={() => refetchTasks?.()}
        wpConnected={wpConnected}
        copilotSession={
          copilotSession
            ? {
                id: copilotSession.id,
                status: copilotSession.status,
                totalCycles: copilotSession.totalCycles,
                currentPosition: copilotSession.currentPosition,
                startingPosition: copilotSession.startingPosition,
                bestPosition: copilotSession.bestPosition,
                targetPosition: copilotSession.targetPosition,
                maxCycles: copilotSession.maxCycles,
                nextCycleAt: copilotSession.nextCycleAt,
              }
            : null
        }
        copilotCycles={copilotCycles}
        onActivateCopilot={() => setIsCopilotModalOpen(true)}
        onPauseCopilot={handlePauseCopilot}
        onResumeCopilot={handleResumeCopilot}
        onRerunCopilotAnalysis={handleRerunAnalysis}
        isRerunning={isRerunning}
        ahrefsKd={ahrefsKd ?? null}
        ahrefsDomainSnapshot={ahrefsDomainSnapshot ?? null}
        ahrefsNewBacklinks={newBacklinks.length}
        ahrefsLostBacklinks={lostBacklinks.length}
      />
      <ChakraProvider theme={chakraExtendedTheme}>
        <ActivateCopilotModal
          open={isCopilotModalOpen}
          onClose={() => setIsCopilotModalOpen(false)}
          onSubmit={handleActivateCopilot}
          keyword={keyword?.keyword ?? ''}
          currentPosition={
            livePosition?.livePosition ?? (keyword?.latestSnapshot?.position || null)
          }
          targetPageSet={!!keyword?.targetPageId}
          targetPageUrl={keyword?.targetPageId ? targetPageUrl : null}
          detectedPageUrl={!keyword?.targetPageId ? targetPageUrl : null}
          onConfirmTargetPage={async () => {
            if (!targetPageUrl || !keywordId) return;
            // Find the seo_pages record for this URL, then save it as target
            const matchingPage = projectPages.find((p) => p.url === targetPageUrl);
            if (matchingPage) {
              await handleTargetPageChange(matchingPage.id, matchingPage.url);
            }
          }}
          wpConnected={wpConnected}
          gscConnected={isConnected}
        />
      </ChakraProvider>
    </>
  );
};

export default SeoKeywordDetailPage;
